import { Button, Input } from '@/components/common'
import Label from '@/components/common/Input/Label'
// import { GoogleIcon } from '@/components/icons'
import Link from 'next/link'
import React, { FC, useEffect, useState } from 'react'
import { signIn, useSession } from 'next-auth/react'
import useTranslation from 'next-translate/useTranslation'
import { useRouter } from 'next/router'
import { UserResponse } from '@/client-sdk'
import { EyeIcon, EyeOffIcon } from 'lucide-react'
import { z } from 'zod'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import GoogleIcon from '../icons/GoogleIcon'

enum IdentityProvider {
	FACEBOOK = 'cognito-facebook',
	GOOGLE = 'cognito-google',
	CUSTOM = 'credentials',
}

const loginSchema = z.object({
	email: z.string().email({ message: 'login.email-error' }),
	password: z.string().min(8, { message: 'login.password-error' }),
})

type LoginFormData = z.infer<typeof loginSchema>

const Login: FC = () => {
	const [showPassword, setShowPassword] = useState(false)
	const [error, setError] = useState<string | null>(null)
	const [loading, setLoading] = useState(false)
	const { t } = useTranslation('login')
	const { data: session, status } = useSession()
	const router = useRouter()

	const {
		register,
		handleSubmit,
		formState: { errors },
	} = useForm<LoginFormData>({
		resolver: zodResolver(loginSchema),
	})

	const isGoogleLoginEnabled = Boolean(Number(process.env.NEXT_PUBLIC_GOOGLE_LOGIN_ENABLED))

	useEffect(() => {
		if (status === 'authenticated' && session && session.user) {
			//@ts-ignore
			const userGroup = session.user.group as unknown as UserResponse.GroupEnum
			if (userGroup === UserResponse.GroupEnum.Admin) {
				router.push('/admin')
			} else if (userGroup === UserResponse.GroupEnum.Manager) {
				router.push('/talent-pool')
			} else {
				router.push('/profile/work-profile')
			}
		}
	}, [session, status, router])

	const togglePasswordVisibility = () => {
		setShowPassword(prev => !prev)
	}

	const onSubmit = async (data: LoginFormData) => {
		setLoading(true)
		const response = await signIn(IdentityProvider.CUSTOM, {
			email: data.email,
			password: data.password,
			redirect: false,
		})

		if (response && response.error) {
			setError('login.invalid-credentials')
			setLoading(false)
		} else {
			setError(null)
		}
	}

	if (status === 'loading') {
		return <div>Loading...</div>
	}

	return (
		<>
			<div className="bg-white h-fit relative rounded-[10px] shadow-container w-[448px]">
				<div className="p-8">
					<div className="flex justify-between ">
						<h2 className="text-lg font-extrabold text-brand-gray-400 uppercase tracking-wider mb-5">
							{t('login.title')}
						</h2>
						<Link className="text-brand-primary-md font-semibold" href={'/register'}>
							{t('login.register')}
						</Link>
					</div>
					<form className="flex flex-col gap-6" onSubmit={handleSubmit(onSubmit)}>
						<div className="relative">
							<Label title={t('login.email')} htmlFor="email" required>
								<Input id="email" placeholder={t('login.email-placeholder')} type="email" {...register('email')} />
							</Label>
							{errors.email && errors.email.message && (
								<span className="pl-4 pt-2">
									<span className="text-red-500 text-xs font-semibold">{t(errors.email.message)}</span>
								</span>
							)}
						</div>
						<div className="relative">
							<Label title={t('login.password')} htmlFor="password" required>
								<div className="flex items-center border border-gray-300 rounded-md">
									<Input
										id="password"
										placeholder={t('login.password-placeholder')}
										type={showPassword ? 'text' : 'password'}
										{...register('password')}
										className="flex-1 border-none focus:ring-0 focus:outline-none"
									/>
									<button
										type="button"
										className="px-3 py-2"
										onClick={togglePasswordVisibility}
										aria-label={showPassword ? t('login.hide-password') : t('login.show-password')}
									>
										{showPassword ? <EyeOffIcon /> : <EyeIcon />}
									</button>
								</div>
							</Label>
							{errors.password && errors.password.message && (
								<span className="pl-4 pt-2">
									<span className="text-red-500 text-xs font-semibold">{t(errors.password.message)}</span>
								</span>
							)}
							{error && <div className="text-red-500 text-xs font-semibold mt-2 pl-4">{t(error)}</div>}
						</div>

						<Link className="font-medium text-sm text-brand-primary-md text-right -mt-4 mb-2" href="/forgot-password">
							{t('login.forgot-password')}
						</Link>
						<Button type="submit"> {loading ? t('login.loading') : t('login.submit')}</Button>
						<Button
							variant="secondary"
							className="text-sm text-brand-primary-md font-semibold"
							onClick={() => router.push('/resend-code')}
						>
							{t('common:resend-code')}
						</Button>
					</form>
					{isGoogleLoginEnabled && (
						<div className="mt-6 flex flex-col gap-6">
							<div className="flex gap-4 items-center">
								<div className="flex-1 h-[1px] bg-[#CCD5DF]" />
								<p className="text-brand-gray-400 font-bold tracking-wide text-xs uppercase text-center">
									{t('login.or')}
								</p>
								<div className="flex-1 h-[1px] bg-[#CCD5DF]" />
							</div>
							<Button
								className="flex items-center justify-center gap-2"
								variant="secondary"
								onClick={() =>
									signIn('cognito_google', {
										callbackUrl: '/profile/work-profile',
									})
								}
							>
								<GoogleIcon /> {t('login.google-login')}
							</Button>
						</div>
					)}
				</div>
			</div>
		</>
	)
}

export default Login
